/**
 * Centralized money / tax / FX math for POS.
 * All arithmetic uses decimal.js — no raw float money math.
 *
 * Rounding: ROUND_HALF_UP to 2 decimal places only for display, API money fields,
 * and final payable comparison. Intermediate tax/FX steps keep full precision.
 */

import Decimal from "decimal.js";

Decimal.set({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -50,
  toExpPos: 50,
});

/** Sub-currency places (e.g. paise / cents) for half-up comparisons */
export const MONEY_DECIMAL_PLACES = 2;

export const toDecimal = (value) => {
  if (value === null || value === undefined || value === "") return new Decimal(0);
  if (value instanceof Decimal) return value;
  return new Decimal(String(value));
};

/**
 * Round half up to 2 decimal places (final display / wire amounts).
 */
export const roundMoneyHalfUp = (value) => {
  return toDecimal(value).toDecimalPlaces(MONEY_DECIMAL_PLACES, Decimal.ROUND_HALF_UP);
};

/**
 * Human / UI string (2 dp). Do not feed this back into calculations — use raw values.
 */
export const formatMoneyDisplay = (value) =>
  roundMoneyHalfUp(value).toFixed(MONEY_DECIMAL_PLACES);

export const toMoneyNumber = (value) => roundMoneyHalfUp(value).toNumber();

/** Line: (unit × qty) − line discount, tax-inclusive gross in pricing currency */
export function calculateItemTotal({ unitPrice, quantity, lineDiscount = 0 }) {
  const gross = toDecimal(unitPrice).mul(toDecimal(quantity)).minus(toDecimal(lineDiscount));
  return gross.lt(0) ? new Decimal(0) : gross;
}

/** Bill-level discount on a subtotal base (taxable or pre-tax per product rules) */
export function applyDiscount(subtotal, discountType, discountValue) {
  const base = toDecimal(subtotal);
  const val = toDecimal(discountValue);
  if (discountType === "Percentage") {
    return base.mul(val).div(100);
  }
  return val;
}

export function cartUsesLineDiscounts(cart = []) {
  return (cart || []).some((item) => toDecimal(item.discountPercent).gt(0));
}

export function getCartLineDiscountPercent(item = {}) {
  const percent = toDecimal(item.discountPercent);
  if (percent.gt(0)) {
    return Decimal.min(100, Decimal.max(0, percent));
  }
  const qty = toDecimal(item.quantity || item.saleQuantity || 0);
  const gross = toDecimal(item.salePrice || 0).mul(qty);
  if (gross.lte(0)) return new Decimal(0);
  const amount = toDecimal(item.discount || 0);
  if (amount.lte(0)) return new Decimal(0);
  return Decimal.min(100, amount.mul(100).div(gross));
}

export function getCartLineDiscountAmount(item = {}) {
  const qty = toDecimal(item.quantity || item.saleQuantity || 0);
  const gross = toDecimal(item.salePrice || 0).mul(qty);
  const percent = toDecimal(item.discountPercent);
  if (percent.gt(0)) {
    return Decimal.min(gross, Decimal.max(0, gross.mul(percent).div(100)));
  }
  return Decimal.min(gross, Decimal.max(0, toDecimal(item.discount || 0)));
}

/**
 * Additive tax on taxable (exclusive) amount — used after discount in non-GST-inclusive paths.
 */
export function applyTax(taxableExclusive, taxRatePercent) {
  return toDecimal(taxableExclusive).mul(toDecimal(taxRatePercent)).div(100);
}

/**
 * GST/VAT included in shelf price: split into exclusive taxable + tax.
 */
export function extractTaxFromInclusiveGross(inclusiveGross, taxRatePercent) {
  const gross = toDecimal(inclusiveGross);
  const rate = toDecimal(taxRatePercent);
  if (rate.lte(0)) {
    return { taxable: gross, tax: new Decimal(0), gross };
  }
  const divisor = rate.div(100).plus(1);
  const taxable = gross.div(divisor);
  const tax = gross.minus(taxable);
  return { taxable, tax, gross };
}

/**
 * FX: same formula as legacy CurrencyContext — amount / rate[from] * rate[to]
 * `rates[currency]` must be defined for each currency used.
 */
export function convertCurrency(amount, fromCurrency, toCurrency, rates) {
  if (fromCurrency === toCurrency) return toDecimal(amount);
  if (!rates) return toDecimal(amount);
  const rFrom = rates[fromCurrency];
  const rTo = rates[toCurrency];
  if (rFrom === undefined || rTo === undefined || rFrom === null || rTo === null) {
    return toDecimal(amount);
  }
  const a = toDecimal(amount);
  return a.div(toDecimal(rFrom)).mul(toDecimal(rTo));
}

/**
 * Pending / due: single source in base currency.
 */
export function calculatePendingAmount(invoiceTotalBase, totalPaidBase) {
  return toDecimal(invoiceTotalBase).minus(toDecimal(totalPaidBase));
}

export function isEffectivelyPaidInFull(amountDue, amountPaid, epsilonPlaces = 4) {
  const due = roundMoneyHalfUp(amountDue);
  const paid = roundMoneyHalfUp(amountPaid);
  const diff = paid.minus(due).abs();
  const eps = new Decimal(1).div(10 ** epsilonPlaces);
  return diff.lte(eps) || paid.gte(due);
}

export function isOverpayment(amountDue, amountPaid) {
  const due = roundMoneyHalfUp(amountDue);
  const paid = roundMoneyHalfUp(amountPaid);
  return paid.gt(due);
}

/**
 * Aggregate item-level groups by tax % (full line, not return-proportioned).
 */
function aggregateTaxGroupsFromItemTaxes(cart, itemTaxes) {
  const taxGroups = {};
  let totalTaxableBeforeDiscount = new Decimal(0);

  cart.forEach((item) => {
    const itemKey = `${item._id}-${item.selectedBatchId}`;
    const taxInfo = itemTaxes[itemKey];
    if (!taxInfo) return;

    const taxRate = new Decimal(taxInfo.taxPercentage || 0);
    const itemTaxableAmount = toDecimal(taxInfo.taxableAmount || 0);
    const key = taxRate.toString();
    if (!taxGroups[key]) {
      taxGroups[key] = {
        taxRate: taxRate.toNumber(),
        taxableAmountBeforeDiscount: new Decimal(0),
      };
    }
    taxGroups[key].taxableAmountBeforeDiscount =
      taxGroups[key].taxableAmountBeforeDiscount.plus(itemTaxableAmount);
    totalTaxableBeforeDiscount = totalTaxableBeforeDiscount.plus(itemTaxableAmount);
  });

  return { taxGroups, totalTaxableBeforeDiscount };
}

function isGstTaxMode(posTaxType) {
  return String(posTaxType || "").toLowerCase() === "gst";
}

/**
 * Inclusive MRP: tax is already embedded in line totals from itemTaxes.
 * Bill discount prorates taxable and tax together, unless cart lines already
 * carry their own discountPercent (itemTaxes are then already discounted).
 */
function buildInclusiveTaxBreakdown(cart, itemTaxes, discountType, discountValue, mode = "sale") {
  const useLineDiscounts = cartUsesLineDiscounts(cart);
  const { totalTaxableBeforeDiscount: aggregatedTaxableBefore } =
    aggregateTaxGroupsFromItemTaxes(cart, itemTaxes);

  const billDiscBase = useLineDiscounts
    ? new Decimal(0)
    : discountType === "Fixed"
      ? toDecimal(discountValue || 0)
      : aggregatedTaxableBefore.mul(toDecimal(discountValue || 0)).div(100);

  const billDiscountRate = useLineDiscounts
    ? new Decimal(0)
    : getBillDiscountRate(aggregatedTaxableBefore.toNumber(), billDiscBase.toNumber());
  const factor = new Decimal(1).minus(billDiscountRate);

  const taxGroups = {};
  let totalTaxableBeforeDiscount = new Decimal(0);
  let totalDiscBase = new Decimal(0);
  let netTaxableValue = new Decimal(0);
  let totalTax = new Decimal(0);
  let totalCGST = new Decimal(0);
  let totalSGST = new Decimal(0);
  let totalIGST = new Decimal(0);
  let totalVAT = new Decimal(0);

  cart.forEach((item) => {
    const itemKey = `${item._id}-${item.selectedBatchId}`;
    const taxInfo = itemTaxes[itemKey];
    if (!taxInfo) return;

    let quantityFactor = new Decimal(1);
    if (mode === "return") {
      const returnQuantity = toDecimal(item.returnQuantity || 0);
      if (returnQuantity.lte(0)) return;
      const denom = toDecimal(item.saleQuantity || item.quantity || 1);
      quantityFactor = denom.gt(0) ? returnQuantity.div(denom) : new Decimal(0);
    }

    const taxRate = new Decimal(taxInfo.taxPercentage || 0);
    let taxableBefore;
    let taxableAfter;
    let taxAfter;
    let cgstAfter;
    let sgstAfter;
    let igstAfter;
    let vatAfter;

    if (useLineDiscounts) {
      const qty = toDecimal(item.quantity || item.saleQuantity || 1);
      const grossBefore = toDecimal(item.salePrice || 0).mul(qty);
      const extracted = extractTaxFromInclusiveGross(grossBefore, taxRate);
      taxableBefore = extracted.taxable.mul(quantityFactor);
      taxableAfter = toDecimal(taxInfo.taxableAmount || 0).mul(quantityFactor);
      taxAfter = toDecimal(taxInfo.tax || 0).mul(quantityFactor);
      cgstAfter = toDecimal(taxInfo.CGST || 0).mul(quantityFactor);
      sgstAfter = toDecimal(taxInfo.SGST || 0).mul(quantityFactor);
      igstAfter = toDecimal(taxInfo.IGST || 0).mul(quantityFactor);
      vatAfter = toDecimal(taxInfo.VAT || 0).mul(quantityFactor);
    } else {
      const lineFactor = factor.mul(quantityFactor);
      taxableBefore = toDecimal(taxInfo.taxableAmount || 0).mul(quantityFactor);
      taxableAfter = taxableBefore.mul(factor);
      taxAfter = toDecimal(taxInfo.tax || 0).mul(lineFactor);
      cgstAfter = toDecimal(taxInfo.CGST || 0).mul(lineFactor);
      sgstAfter = toDecimal(taxInfo.SGST || 0).mul(lineFactor);
      igstAfter = toDecimal(taxInfo.IGST || 0).mul(lineFactor);
      vatAfter = toDecimal(taxInfo.VAT || 0).mul(lineFactor);
    }

    const key = taxRate.toString();
    if (!taxGroups[key]) {
      taxGroups[key] = {
        taxRate: taxRate.toNumber(),
        taxableAmountBeforeDiscount: new Decimal(0),
        itemCount: 0,
        taxableAmountAfterDiscount: new Decimal(0),
        CGST: new Decimal(0),
        SGST: new Decimal(0),
        IGST: new Decimal(0),
        VAT: new Decimal(0),
        totalTax: new Decimal(0),
        totalAmount: new Decimal(0),
      };
    }

    const group = taxGroups[key];
    group.taxableAmountBeforeDiscount = group.taxableAmountBeforeDiscount.plus(taxableBefore);
    group.taxableAmountAfterDiscount = group.taxableAmountAfterDiscount.plus(taxableAfter);
    group.CGST = group.CGST.plus(cgstAfter);
    group.SGST = group.SGST.plus(sgstAfter);
    group.IGST = group.IGST.plus(igstAfter);
    group.VAT = group.VAT.plus(vatAfter);
    group.totalTax = group.totalTax.plus(taxAfter);
    group.totalAmount = group.totalAmount.plus(taxableAfter).plus(taxAfter);
    group.itemCount += 1;

    netTaxableValue = netTaxableValue.plus(taxableAfter);
    totalTax = totalTax.plus(taxAfter);
    totalCGST = totalCGST.plus(cgstAfter);
    totalSGST = totalSGST.plus(sgstAfter);
    totalIGST = totalIGST.plus(igstAfter);
    totalVAT = totalVAT.plus(vatAfter);
    totalTaxableBeforeDiscount = totalTaxableBeforeDiscount.plus(taxableBefore);
    totalDiscBase = totalDiscBase.plus(Decimal.max(0, taxableBefore.minus(taxableAfter)));
  });

  if (!useLineDiscounts) {
    totalTaxableBeforeDiscount = aggregatedTaxableBefore;
    totalDiscBase = billDiscBase;
  }

  return {
    totalDiscBase,
    totalTaxableBeforeDiscount,
    netTaxableValue,
    totalTax,
    totalCGST,
    totalSGST,
    totalIGST,
    totalVAT,
    taxGroups,
  };
}

/**
 * Full-cart invoice totals after bill discount + tax recompute (matches prior taxCalculations.js).
 * Return flows must pass itemTaxes already scaled to returned quantities (see useReturnPaymentManager).
 * Returns numeric fields (full precision from Decimal), not rounded at line level.
 */
export function calculateInvoiceTotal(
  cart,
  itemTaxes,
  discountType,
  discountValue,
  transportCost,
  posTaxType,
  isSameState
) {
  const breakdown = buildInclusiveTaxBreakdown(cart, itemTaxes, discountType, discountValue, "sale");
  const tCost = toDecimal(transportCost || 0);
  const grandTotal = breakdown.netTaxableValue.plus(breakdown.totalTax).plus(tCost);

  return {
    subtotal: breakdown.totalTaxableBeforeDiscount,
    discount: breakdown.totalDiscBase,
    transport: tCost,
    taxable: breakdown.netTaxableValue,
    tax: breakdown.totalTax,
    CGST: breakdown.totalCGST,
    SGST: breakdown.totalSGST,
    IGST: breakdown.totalIGST,
    VAT: breakdown.totalVAT,
    netTotal: grandTotal,
  };
}

/** Plain numbers for existing UI / payloads */
export function calculateCartTotals(
  cart,
  itemTaxes,
  discountType,
  discountValue,
  transportCost,
  posTaxType,
  isSameState
) {
  const t = calculateInvoiceTotal(
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    posTaxType,
    isSameState
  );
  return {
    subtotal: t.subtotal.toNumber(),
    discount: t.discount.toNumber(),
    transport: t.transport.toNumber(),
    taxable: t.taxable.toNumber(),
    tax: t.tax.toNumber(),
    CGST: t.CGST.toNumber(),
    SGST: t.SGST.toNumber(),
    IGST: t.IGST.toNumber(),
    VAT: t.VAT.toNumber(),
    netTotal: t.netTotal.toNumber(),
  };
}

const decObjToNumbers = (o) => {
  const out = {};
  Object.keys(o).forEach((k) => {
    const v = o[k];
    if (v instanceof Decimal) {
      out[k] = v.toNumber();
    } else {
      out[k] = v;
    }
  });
  return out;
};

/**
 * Per-item tax for inclusive-POS (MRP includes GST).
 */
export function calculateItemTaxRow(item, pharmacyStateCode, patientStateCode, posTaxType) {
  const unitPrice = toDecimal(item.salePrice || 0);
  const quantity = toDecimal(item.quantity || 1);
  const lineDiscount = getCartLineDiscountAmount(item);
  const totalAmount = unitPrice.mul(quantity);
  const netTotalAmount = Decimal.max(0, totalAmount.minus(lineDiscount));

  const taxPercentage = new Decimal(item.taxPercentage || 0);

  const { taxable, tax: totalTax } = extractTaxFromInclusiveGross(netTotalAmount, taxPercentage);

  const isSameState =
    !patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

  let isIGST = false;
  let IGST = new Decimal(0);
  let CGST = new Decimal(0);
  let SGST = new Decimal(0);
  let VAT = new Decimal(0);

  if (isGstTaxMode(posTaxType)) {
    if (isSameState) {
      CGST = totalTax.div(2);
      SGST = totalTax.div(2);
    } else {
      IGST = totalTax;
      isIGST = true;
    }
  } else {
    VAT = totalTax;
  }

  return decObjToNumbers({
    taxPercentage: taxPercentage,
    tax: totalTax,
    IGST,
    CGST,
    SGST,
    VAT,
    isIGST,
    IGSTPercentage: isIGST ? taxPercentage : new Decimal(0),
    CGSTPercentage: !isIGST && posTaxType === "gst" ? taxPercentage.div(2) : new Decimal(0),
    SGSTPercentage: !isIGST && posTaxType === "gst" ? taxPercentage.div(2) : new Decimal(0),
    taxableAmount: taxable,
    itemSubtotal: totalAmount,
    discount: lineDiscount,
  });
}

/**
 * Return items with stored % — inclusive gross split; optional CGST/SGST weighting.
 */
export function calculateReturnItemTaxRow(item, pharmacyStateCode, patientStateCode, posTaxType) {
  if (item.taxPercentage === undefined) {
    return null;
  }
  const unitPrice = toDecimal(item.salePrice || 0);
  const quantity = toDecimal(item.quantity || 1);
  const totalAmount = unitPrice.mul(quantity);
  const taxPercentage = toDecimal(item.taxPercentage || 0);
  const CGSTPercentage = toDecimal(item.CGSTPercentage || 0);
  const SGSTPercentage = toDecimal(item.SGSTPercentage || 0);
  const IGSTPercentage = toDecimal(item.IGSTPercentage || 0);

  const { taxable, tax: totalTax } = extractTaxFromInclusiveGross(totalAmount, taxPercentage);

  const isSameState =
    !patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

  let CGST = new Decimal(0);
  let SGST = new Decimal(0);
  let IGST = new Decimal(0);
  let VAT = new Decimal(0);

  if (posTaxType === "gst") {
    if (isSameState) {
      if (CGSTPercentage.gt(0) || SGSTPercentage.gt(0)) {
        const totalP = CGSTPercentage.plus(SGSTPercentage);
        const denom = totalP.gt(0) ? totalP : new Decimal(1);
        CGST = totalTax.mul(CGSTPercentage).div(denom);
        SGST = totalTax.mul(SGSTPercentage).div(denom);
      } else {
        CGST = totalTax.div(2);
        SGST = totalTax.div(2);
      }
    } else {
      IGST = totalTax;
    }
  } else if (posTaxType === "vat") {
    VAT = totalTax;
  }

  return {
    ...decObjToNumbers({
      taxPercentage,
      CGSTPercentage,
      SGSTPercentage,
      IGSTPercentage,
      tax: totalTax,
      CGST,
      SGST,
      IGST,
      VAT,
      taxableAmount: taxable,
    }),
    isIGST: item.isIGST || false,
  };
}

/**
 * Bill discount rate: discount / pre-discount taxable subtotal
 */
export function getBillDiscountRate(totalsSubtotal, totalsDiscount) {
  const s = toDecimal(totalsSubtotal);
  if (s.lte(0)) return new Decimal(0);
  return toDecimal(totalsDiscount).div(s);
}

/**
 * Item-level post-discount line for invoice API (proportional bill discount on taxable)
 */
export function getItemLineAfterBillDiscount(taxableBefore, billDiscountRate) {
  return toDecimal(taxableBefore).mul(new Decimal(1).minus(billDiscountRate));
}

/**
 * Recompute per-line tax on exclusive taxable (GST add-on to discounted taxable)
 */
export function computeItemTaxOnExclusiveTaxable(taxableAfterDiscount, taxRate, posTaxType, isSameState) {
  const t = toDecimal(taxableAfterDiscount);
  const tr = toDecimal(taxRate || 0);
  let itemTax = new Decimal(0);
  let itemCGST = new Decimal(0);
  let itemSGST = new Decimal(0);
  let itemIGST = new Decimal(0);
  let itemVAT = new Decimal(0);
  if (posTaxType === "gst") {
    if (isSameState) {
      itemTax = t.mul(tr).div(100);
      itemCGST = itemTax.div(2);
      itemSGST = itemTax.div(2);
    } else {
      itemTax = t.mul(tr).div(100);
      itemIGST = itemTax;
    }
  } else if (posTaxType === "vat") {
    itemVAT = t.mul(tr).div(100);
    itemTax = itemVAT;
  }
  return { itemTax, itemCGST, itemSGST, itemIGST, itemVAT };
}

/**
 * One source for CartSummary tax table (Sales + Return) — no duplicate float logic.
 * @param {'sale'|'return'} mode
 */
export function getBillTaxBreakdownRows(
  cart,
  itemTaxes,
  totals,
  posTaxType,
  isSameState,
  mode = "sale",
  discountType = "Percentage",
  discountValue = 0
) {
  const breakdown = buildInclusiveTaxBreakdown(
    cart,
    itemTaxes,
    discountType,
    discountValue,
    mode
  );

  return Object.values(breakdown.taxGroups)
    .filter((g) => g.taxRate >= 0)
    .sort((a, b) => a.taxRate - b.taxRate)
    .map((group) => ({
      taxRate: group.taxRate,
      itemCount: group.itemCount,
      taxableAmountBeforeDiscount: group.taxableAmountBeforeDiscount.toNumber(),
      taxableAmountAfterDiscount: group.taxableAmountAfterDiscount.toNumber(),
      taxableAmount: group.taxableAmountAfterDiscount.toNumber(),
      netTaxableAmount: group.taxableAmountAfterDiscount.toNumber(),
      CGST: group.CGST.toNumber(),
      SGST: group.SGST.toNumber(),
      IGST: group.IGST.toNumber(),
      VAT: group.VAT.toNumber(),
      totalTax: group.totalTax.toNumber(),
      totalAmount: group.totalAmount.toNumber(),
    }));
}
